import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
    Globe,
    Folder,
    FolderOpen,
    User,
    Monitor,
    Users,
    Search,
    ChevronRight,
    ChevronDown,
    Loader2,
    ShieldAlert,
    Shield,
    Hash,
    List,
} from 'lucide-react';
import type { ADDomain, ADOU, ADUser, ADComputer, ADGroup } from './adTypes';
import baseapi from '../../services/baseapi';

interface ADTreeViewProps {
    domain: ADDomain | null;
    ous: ADOU[];
    users: ADUser[];
    computers: ADComputer[];
    groups: ADGroup[];
}

export default function ADTreeView({ domain, ous, users: initialUsers, computers: initialComputers, groups: initialGroups }: ADTreeViewProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({ domain: true });
    const [selectedObject, setSelectedObject] = useState<any>({
        type: 'domain',
        name: domain?.domain || (domain ? 'Domain Root' : 'Active Directory Root'),
        data: domain,
    });

    // Local cache for lazy loaded OU child objects with pagination flags
    const [ouCache, setOuCache] = useState<Record<string, {
        users: ADUser[];
        computers: ADComputer[];
        groups: ADGroup[];
        totalUsers?: number;
        totalComputers?: number;
        totalGroups?: number;
        hasMoreUsers?: boolean;
        hasMoreComputers?: boolean;
        hasMoreGroups?: boolean;
    }>>({});
    const [loadingOu, setLoadingOu] = useState(false);

    // Search & infinite scroll states for tree folders (users, computers, groups)
    const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
    const [searchResults, setSearchResults] = useState<Record<string, any[]>>({});
    const [searchLoading, setSearchLoading] = useState<Record<string, boolean>>({});
    const [searchHasMore, setSearchHasMore] = useState<Record<string, boolean>>({});
    const [folderTotals, setFolderTotals] = useState<Record<string, number>>({});
    const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({});
    const searchTimers = useRef<Record<string, any>>({});

    const PAGE_SIZE = 50;

    // Read asset_id from session storage
    const info = JSON.parse(sessionStorage.getItem('internal_ids') || '{}');
    const asset_id = info.asset_id;

    const handleSearchObjects = useCallback((folderKey: string, ouName: string, term: string, kind: 'users' | 'computers' | 'groups', isContainer = false) => {
        setSearchTerms(prev => ({ ...prev, [folderKey]: term }));

        if (searchTimers.current[folderKey]) {
            clearTimeout(searchTimers.current[folderKey]);
        }

        if (!term.trim()) {
            setSearchResults(prev => {
                const next = { ...prev };
                delete next[folderKey];
                return next;
            });
            setSearchHasMore(prev => ({ ...prev, [folderKey]: false }));
            return;
        }

        setSearchLoading(prev => ({ ...prev, [folderKey]: true }));

        searchTimers.current[folderKey] = setTimeout(async () => {
            try {
                const queryObj: any = {
                    asset_id: parseInt(asset_id, 10) || undefined,
                    is_deleted: false,
                };

                if (!isContainer) {
                    queryObj.ou_name = ouName;
                } else {
                    queryObj.ou_name = "";
                }

                if (kind === 'users') {
                    queryObj.$or = [
                        { name: { $ilike: `%${term.trim()}%` } },
                        { sam_account_name: { $ilike: `%${term.trim()}%` } },
                        { display_name: { $ilike: `%${term.trim()}%` } },
                        { user_principal_name: { $ilike: `%${term.trim()}%` } },
                        { mail: { $ilike: `%${term.trim()}%` } },
                        { distinguished_name: { $ilike: `%${term.trim()}%` } }
                    ];
                } else if (kind === 'computers') {
                    queryObj.$or = [
                        { name: { $ilike: `%${term.trim()}%` } },
                        { sam_account_name: { $ilike: `%${term.trim()}%` } },
                        { dns_host_name: { $ilike: `%${term.trim()}%` } },
                        { operating_system: { $ilike: `%${term.trim()}%` } },
                        { distinguished_name: { $ilike: `%${term.trim()}%` } }
                    ];
                } else if (kind === 'groups') {
                    queryObj.$or = [
                        { name: { $ilike: `%${term.trim()}%` } },
                        { sam_account_name: { $ilike: `%${term.trim()}%` } },
                        { common_name: { $ilike: `%${term.trim()}%` } },
                        { description: { $ilike: `%${term.trim()}%` } },
                        { distinguished_name: { $ilike: `%${term.trim()}%` } }
                    ];
                }

                const endpoint = kind === 'users' ? '/ad_users' : kind === 'computers' ? '/ad_computers' : '/ad_groups';
                const queryStr = encodeURIComponent(JSON.stringify(queryObj));
                const res = await baseapi.get(`${endpoint}?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`);
                if (res.data?.status && Array.isArray(res.data.data)) {
                    let results: any[] = res.data.data;
                    const totalCount = res.data.total ?? res.data.count ?? results.length;
                    if (isContainer) {
                        results = results.filter(item => matchContainerObject(item, ouName));
                    }
                    setSearchResults(prev => ({ ...prev, [folderKey]: results }));
                    setFolderTotals(prev => ({ ...prev, [folderKey]: totalCount }));
                    setSearchHasMore(prev => ({ ...prev, [folderKey]: res.data.data.length >= PAGE_SIZE }));
                } else {
                    setSearchResults(prev => ({ ...prev, [folderKey]: [] }));
                    setFolderTotals(prev => ({ ...prev, [folderKey]: 0 }));
                    setSearchHasMore(prev => ({ ...prev, [folderKey]: false }));
                }
            } catch (err) {
                console.error(`Failed to search ${kind}:`, err);
            } finally {
                setSearchLoading(prev => ({ ...prev, [folderKey]: false }));
            }
        }, 300);
    }, [asset_id]);

    const toggleExpand = (id: string, e?: React.MouseEvent, defaultState = false) => {
        if (e) e.stopPropagation();
        setExpandedNodes(prev => {
            const current = prev[id] !== undefined ? prev[id] : defaultState;
            return { ...prev, [id]: !current };
        });
    };

    const isNodeExpanded = (id: string, defaultState = true) => {
        return expandedNodes[id] !== undefined ? expandedNodes[id] : defaultState;
    };

    // Lazy load child objects for an OU on click
    const loadOUDetails = useCallback(async (ou: ADOU) => {
        const ouName = ou.name;
        if (ouCache[ouName]) return ouCache[ouName];

        setLoadingOu(true);
        try {
            const queryObj = { asset_id: parseInt(asset_id, 10) || undefined, is_deleted: false, ou_name: ouName };
            const queryStr = encodeURIComponent(JSON.stringify(queryObj));

            const [uRes, cRes, gRes] = await Promise.allSettled([
                baseapi.get(`/ad_users?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`),
                baseapi.get(`/ad_computers?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`),
                baseapi.get(`/ad_groups?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`),
            ]);

            const resUsers: ADUser[] = uRes.status === 'fulfilled' && uRes.value?.data?.data ? uRes.value.data.data : [];
            const resComputers: ADComputer[] = cRes.status === 'fulfilled' && cRes.value?.data?.data ? cRes.value.data.data : [];
            const resGroups: ADGroup[] = gRes.status === 'fulfilled' && gRes.value?.data?.data ? gRes.value.data.data : [];

            const totalUsers = uRes.status === 'fulfilled' ? (uRes.value?.data?.total ?? uRes.value?.data?.count ?? resUsers.length) : resUsers.length;
            const totalComputers = cRes.status === 'fulfilled' ? (cRes.value?.data?.total ?? cRes.value?.data?.count ?? resComputers.length) : resComputers.length;
            const totalGroups = gRes.status === 'fulfilled' ? (gRes.value?.data?.total ?? gRes.value?.data?.count ?? resGroups.length) : resGroups.length;

            const result = {
                users: resUsers,
                computers: resComputers,
                groups: resGroups,
                totalUsers,
                totalComputers,
                totalGroups,
                hasMoreUsers: resUsers.length >= PAGE_SIZE,
                hasMoreComputers: resComputers.length >= PAGE_SIZE,
                hasMoreGroups: resGroups.length >= PAGE_SIZE,
            };
            setOuCache(prev => ({ ...prev, [ouName]: result }));
            return result;
        } catch (err) {
            console.warn(`Failed to fetch OU details for ${ouName}:`, err);
            return { users: [], computers: [], groups: [], totalUsers: 0, totalComputers: 0, totalGroups: 0, hasMoreUsers: false, hasMoreComputers: false, hasMoreGroups: false };
        } finally {
            setLoadingOu(false);
        }
    }, [asset_id, ouCache]);

    // Lazy load child objects for a CN Container on click
    const loadContainerDetails = useCallback(async (containerName: string) => {
        const cacheKey = `cn-${containerName}`;
        if (ouCache[cacheKey]) return ouCache[cacheKey];

        setLoadingOu(true);
        try {
            const queryObj = { asset_id: parseInt(asset_id, 10) || undefined, is_deleted: false, ou_name: "" };
            const queryStr = encodeURIComponent(JSON.stringify(queryObj));

            const [uRes, cRes, gRes] = await Promise.allSettled([
                baseapi.get(`/ad_users?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`),
                baseapi.get(`/ad_computers?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`),
                baseapi.get(`/ad_groups?query=${queryStr}&limit=${PAGE_SIZE}&skip=0`),
            ]);

            let resUsers: ADUser[] = uRes.status === 'fulfilled' && uRes.value?.data?.data ? uRes.value.data.data : [];
            let resComputers: ADComputer[] = cRes.status === 'fulfilled' && cRes.value?.data?.data ? cRes.value.data.data : [];
            let resGroups: ADGroup[] = gRes.status === 'fulfilled' && gRes.value?.data?.data ? gRes.value.data.data : [];

            const totalUsers = uRes.status === 'fulfilled' ? (uRes.value?.data?.total ?? uRes.value?.data?.count ?? resUsers.length) : resUsers.length;
            const totalComputers = cRes.status === 'fulfilled' ? (cRes.value?.data?.total ?? cRes.value?.data?.count ?? resComputers.length) : resComputers.length;
            const totalGroups = gRes.status === 'fulfilled' ? (gRes.value?.data?.total ?? gRes.value?.data?.count ?? resGroups.length) : resGroups.length;

            const filteredUsers = resUsers.filter(u => matchContainerObject(u, containerName));
            const filteredComputers = resComputers.filter(c => matchContainerObject(c, containerName));
            const filteredGroups = resGroups.filter(g => matchContainerObject(g, containerName));

            const result = {
                users: filteredUsers,
                computers: filteredComputers,
                groups: filteredGroups,
                totalUsers,
                totalComputers,
                totalGroups,
                hasMoreUsers: resUsers.length >= PAGE_SIZE,
                hasMoreComputers: resComputers.length >= PAGE_SIZE,
                hasMoreGroups: resGroups.length >= PAGE_SIZE,
            };
            setOuCache(prev => ({ ...prev, [cacheKey]: result }));
            return result;
        } catch (err) {
            console.warn(`Failed to fetch container details for ${containerName}:`, err);
            return { users: [], computers: [], groups: [], totalUsers: 0, totalComputers: 0, totalGroups: 0, hasMoreUsers: false, hasMoreComputers: false, hasMoreGroups: false };
        } finally {
            setLoadingOu(false);
        }
    }, [asset_id, ouCache]);

    // Load next page on scroll
    const loadMoreItems = useCallback(async (folderKey: string, ouOrContainerName: string, kind: 'users' | 'computers' | 'groups', isContainer = false) => {
        if (loadingMore[folderKey]) return;

        const term = (searchTerms[folderKey] || '').trim();
        const endpoint = kind === 'users' ? '/ad_users' : kind === 'computers' ? '/ad_computers' : '/ad_groups';
        const cacheKey = isContainer ? `cn-${ouOrContainerName}` : ouOrContainerName;

        if (term) {
            if (searchHasMore[folderKey] === false) return;
        } else {
            const currentCached = ouCache[cacheKey];
            if (currentCached) {
                const hasMore = kind === 'users' ? currentCached.hasMoreUsers : kind === 'computers' ? currentCached.hasMoreComputers : currentCached.hasMoreGroups;
                if (hasMore === false) return;
            }
        }

        setLoadingMore(prev => ({ ...prev, [folderKey]: true }));

        try {
            const queryObj: any = {
                asset_id: parseInt(asset_id, 10) || undefined,
                is_deleted: false,
                ou_name: isContainer ? "" : ouOrContainerName,
            };

            if (term) {
                if (kind === 'users') {
                    queryObj.$or = [
                        { name: { $ilike: `%${term}%` } },
                        { sam_account_name: { $ilike: `%${term}%` } },
                        { display_name: { $ilike: `%${term}%` } },
                        { user_principal_name: { $ilike: `%${term}%` } },
                        { mail: { $ilike: `%${term}%` } },
                        { distinguished_name: { $ilike: `%${term}%` } }
                    ];
                } else if (kind === 'computers') {
                    queryObj.$or = [
                        { name: { $ilike: `%${term}%` } },
                        { sam_account_name: { $ilike: `%${term}%` } },
                        { dns_host_name: { $ilike: `%${term}%` } },
                        { operating_system: { $ilike: `%${term}%` } },
                        { distinguished_name: { $ilike: `%${term}%` } }
                    ];
                } else if (kind === 'groups') {
                    queryObj.$or = [
                        { name: { $ilike: `%${term}%` } },
                        { sam_account_name: { $ilike: `%${term}%` } },
                        { common_name: { $ilike: `%${term}%` } },
                        { description: { $ilike: `%${term}%` } },
                        { distinguished_name: { $ilike: `%${term}%` } }
                    ];
                }
            }

            const currentCount = term
                ? (searchResults[folderKey]?.length || 0)
                : (ouCache[cacheKey]?.[kind]?.length || 0);

            const queryStr = encodeURIComponent(JSON.stringify(queryObj));
            const res = await baseapi.get(`${endpoint}?query=${queryStr}&limit=${PAGE_SIZE}&skip=${currentCount}`);

            if (res.data?.status && Array.isArray(res.data.data)) {
                let newItems = res.data.data;
                const totalCount = res.data.total ?? res.data.count;
                if (isContainer) {
                    newItems = newItems.filter((item: any) => matchContainerObject(item, ouOrContainerName));
                }

                const rawHasMore = res.data.data.length >= PAGE_SIZE;

                if (term) {
                    setSearchResults(prev => {
                        const prevList = prev[folderKey] || [];
                        const merged = [...prevList, ...newItems].filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);
                        return { ...prev, [folderKey]: merged };
                    });
                    if (totalCount !== undefined) {
                        setFolderTotals(prev => ({ ...prev, [folderKey]: totalCount }));
                    }
                    setSearchHasMore(prev => ({ ...prev, [folderKey]: rawHasMore }));
                } else {
                    setOuCache(prev => {
                        const prevCached = prev[cacheKey] || { users: [], computers: [], groups: [] };
                        const prevList = prevCached[kind] || [];
                        const merged = [...prevList, ...newItems].filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);
                        return {
                            ...prev,
                            [cacheKey]: {
                                ...prevCached,
                                [kind]: merged,
                                ...(kind === 'users'
                                    ? { hasMoreUsers: rawHasMore, ...(totalCount !== undefined ? { totalUsers: totalCount } : {}) }
                                    : kind === 'computers'
                                    ? { hasMoreComputers: rawHasMore, ...(totalCount !== undefined ? { totalComputers: totalCount } : {}) }
                                    : { hasMoreGroups: rawHasMore, ...(totalCount !== undefined ? { totalGroups: totalCount } : {}) })
                            }
                        };
                    });
                }
            }
        } catch (err) {
            console.error(`Failed to load more ${kind}:`, err);
        } finally {
            setLoadingMore(prev => ({ ...prev, [folderKey]: false }));
        }
    }, [asset_id, searchTerms, searchHasMore, searchResults, ouCache, loadingMore]);

    const handleFolderScroll = useCallback((e: React.UIEvent<HTMLDivElement>, folderKey: string, name: string, kind: 'users' | 'computers' | 'groups', isContainer = false) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            loadMoreItems(folderKey, name, kind, isContainer);
        }
    }, [loadMoreItems]);

    // Handle selecting an OU: Opens details on right side AND toggles OU folder in sidebar tree
    const handleSelectOU = (ou: ADOU, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const ouKey = `ou-${ou.object_guid}`;
        setSelectedObject({ type: 'ou', name: ou.name, data: ou });
        const willExpand = !isNodeExpanded(ouKey, false);
        setExpandedNodes(prev => ({ ...prev, [ouKey]: willExpand }));
        if (willExpand && !ouCache[ou.name]) {
            loadOUDetails(ou);
        }
    };

    // Handle expanding/collapsing OU via chevron button
    const handleToggleOU = (ou: ADOU, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const ouKey = `ou-${ou.object_guid}`;
        const willExpand = !isNodeExpanded(ouKey, false);
        setExpandedNodes(prev => ({ ...prev, [ouKey]: willExpand }));
        if (willExpand && !ouCache[ou.name]) {
            loadOUDetails(ou);
        }
    };

    // Handle selecting a CN Container: Opens details on right side AND toggles container in sidebar tree
    const handleSelectContainer = (c: any, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const cKey = `cn-${c.name}`;
        setSelectedObject({ type: 'container', name: `CN=${c.name}`, data: c });
        const willExpand = !isNodeExpanded(cKey, false);
        setExpandedNodes(prev => ({ ...prev, [cKey]: willExpand }));
        if (willExpand && !ouCache[cKey]) {
            loadContainerDetails(c.name);
        }
    };

    // Handle expanding/collapsing CN Container via chevron button
    const handleToggleContainer = (c: any, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const cKey = `cn-${c.name}`;
        const willExpand = !isNodeExpanded(cKey, false);
        setExpandedNodes(prev => ({ ...prev, [cKey]: willExpand }));
        if (willExpand && !ouCache[cKey]) {
            loadContainerDetails(c.name);
        }
    };

    // Helper to get parent CN container name from distinguished_name (same as _parentCNName in ad_topology.html)
    const getParentCNName = (dn?: string): string => {
        if (!dn) return '';
        const p = (dn.split(',')[1] || '').trim();
        return /^CN=/i.test(p) ? p.slice(3) : '';
    };

    // Helper to check if an AD object belongs to a built-in CN container (Users, Computers, Builtin)
    const matchContainerObject = (item: { ou_name?: string; distinguished_name?: string }, containerName: string) => {
        const parentCN = getParentCNName(item.distinguished_name);
        if (parentCN) {
            return parentCN.toLowerCase() === containerName.toLowerCase();
        }
        const cLower = containerName.toLowerCase();
        const ouName = (item.ou_name || '').trim();
        if (ouName && ouName !== '-' && ouName.toLowerCase() === cLower) {
            return true;
        }
        return false;
    };

    // Filter tree by search term
    const filteredOus = useMemo(() => {
        if (!searchTerm) return ous;
        const term = searchTerm.toLowerCase();
        return ous.filter(o => o.name.toLowerCase().includes(term) || (o.distinguished_name && o.distinguished_name.toLowerCase().includes(term)));
    }, [ous, searchTerm]);

    // Compute built-in CN Containers (Users, Computers, Builtin) from domain or defaults
    const cnContainers = useMemo(() => {
        if (domain?.cn_containers && domain.cn_containers.length > 0) {
            return domain.cn_containers;
        }
        return [
            { name: 'Users', user_count: initialUsers.filter(u => matchContainerObject(u, 'Users')).length, computer_count: 0, group_count: initialGroups.filter(g => matchContainerObject(g, 'Users')).length },
            { name: 'Computers', user_count: 0, computer_count: initialComputers.filter(c => matchContainerObject(c, 'Computers')).length, group_count: 0 },
            { name: 'Builtin', user_count: 0, computer_count: 0, group_count: initialGroups.filter(g => matchContainerObject(g, 'Builtin')).length },
        ];
    }, [domain?.cn_containers, initialUsers, initialComputers, initialGroups]);

    // Compute child objects for currently selected OU or CN Container
    const currentChildren = useMemo(() => {
        if (!selectedObject || (selectedObject.type !== 'ou' && selectedObject.type !== 'container')) return [];

        if (selectedObject.type === 'ou') {
            const ouName = selectedObject.data?.name;
            const cached = ouCache[ouName];

            let ouUsers = cached ? cached.users : initialUsers.filter(u => u.ou_name && u.ou_name.trim() !== "" && u.ou_name !== "-" ? u.ou_name.toLowerCase() === ouName?.toLowerCase() : (u.distinguished_name && selectedObject.data?.distinguished_name && u.distinguished_name.toLowerCase().includes(selectedObject.data.distinguished_name.toLowerCase())));
            let ouComputers = cached ? cached.computers : initialComputers.filter(c => c.ou_name && c.ou_name.trim() !== "" && c.ou_name !== "-" ? c.ou_name.toLowerCase() === ouName?.toLowerCase() : (c.distinguished_name && selectedObject.data?.distinguished_name && c.distinguished_name.toLowerCase().includes(selectedObject.data.distinguished_name.toLowerCase())));
            let ouGroups = cached ? cached.groups : initialGroups.filter(g => g.ou_name && g.ou_name.trim() !== "" && g.ou_name !== "-" ? g.ou_name.toLowerCase() === ouName?.toLowerCase() : (g.distinguished_name && selectedObject.data?.distinguished_name && g.distinguished_name.toLowerCase().includes(selectedObject.data.distinguished_name.toLowerCase())));

            return [
                ...ouUsers.map(u => ({ ...u, _kind: 'user' })),
                ...ouComputers.map(c => ({ ...c, _kind: 'computer' })),
                ...ouGroups.map(g => ({ ...g, _kind: 'group' })),
            ];
        } else {
            // selType === 'container'
            const cName = selectedObject.data?.name || selectedObject.name?.replace(/^CN=/i, '');
            const cachedC = ouCache[`cn-${cName}`];

            const allUsers = cachedC ? [...initialUsers, ...cachedC.users] : initialUsers;
            const allComputers = cachedC ? [...initialComputers, ...cachedC.computers] : initialComputers;
            const allGroups = cachedC ? [...initialGroups, ...cachedC.groups] : initialGroups;

            const uList = allUsers.filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);
            const cList = allComputers.filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);
            const gList = allGroups.filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);

            const cUsers = uList.filter(u => matchContainerObject(u, cName));
            const cComputers = cList.filter(comp => matchContainerObject(comp, cName));
            const cGroups = gList.filter(g => matchContainerObject(g, cName));

            return [
                ...cUsers.map(u => ({ ...u, _kind: 'user' })),
                ...cComputers.map(c => ({ ...c, _kind: 'computer' })),
                ...cGroups.map(g => ({ ...g, _kind: 'group' })),
            ];
        }
    }, [selectedObject, initialUsers, initialComputers, initialGroups, ouCache]);

    // Handle clicking breadcrumb segments to navigate back to parent OU/container/domain
    const handleBreadcrumbClick = (part?: string, isDomain = false) => {
        if (isDomain) {
            setSelectedObject({ type: 'domain', name: domain?.domain || 'Domain Root', data: domain });
            return;
        }
        if (!part) return;

        const cleanPart = part.replace(/^(OU|CN)=/i, '').trim();
        const isOuType = /^OU=/i.test(part);
        const isCnType = /^CN=/i.test(part);

        // 1. Try matching an Organizational Unit
        const targetOu = ous.find(o =>
            o.name.toLowerCase() === cleanPart.toLowerCase() ||
            (o.distinguished_name && o.distinguished_name.toLowerCase().includes(part.toLowerCase()))
        );

        if (targetOu && (isOuType || !isCnType)) {
            const ouKey = `ou-${targetOu.object_guid}`;
            setSelectedObject({ type: 'ou', name: targetOu.name, data: targetOu });
            setExpandedNodes(prev => ({ ...prev, [ouKey]: true }));
            if (!ouCache[targetOu.name]) {
                loadOUDetails(targetOu);
            }
            return;
        }

        // 2. Try matching a Built-in CN Container (Users, Computers, Builtin)
        const targetCn = cnContainers.find(c =>
            c.name.toLowerCase() === cleanPart.toLowerCase()
        );

        if (targetCn) {
            const cKey = `cn-${targetCn.name}`;
            setSelectedObject({ type: 'container', name: `CN=${targetCn.name}`, data: targetCn });
            setExpandedNodes(prev => ({ ...prev, [cKey]: true }));
            if (!ouCache[cKey]) {
                loadContainerDetails(targetCn.name);
            }
            return;
        }

        // 3. Fallback: Search initial groups
        const targetGroup = initialGroups.find(g => (g.name || g.common_name)?.toLowerCase() === cleanPart.toLowerCase());
        if (targetGroup) {
            setSelectedObject({ type: 'group', name: targetGroup.name || targetGroup.common_name, data: targetGroup });
            return;
        }
    };

    // Breadcrumb parser
    const renderBreadcrumb = (dn?: string, name?: string) => {
        if (!dn && !name) return null;
        const parts = dn ? dn.split(',').map(p => p.trim()) : [];
        const dcParts = parts.filter(p => /^DC=/i.test(p)).map(p => p.slice(3));
        const nonDcParts = parts.filter(p => !/^DC=/i.test(p)).slice(1).reverse();
        const domainStr = dcParts.join('.');

        return (
            <div className="flex items-center flex-wrap gap-1 text-xs text-gray-200 font-mono py-1.5 border-b border-gray-800 mb-3">
                <button
                    type="button"
                    onClick={() => handleBreadcrumbClick(undefined, true)}
                    className="text-orange-400 font-bold flex items-center gap-1 hover:text-orange-300 hover:underline cursor-pointer transition-colors bg-transparent border-0 p-0"
                    title="Go to Domain Root"
                >
                    <Globe size={12} /> {domainStr || domain?.domain || 'domain'}
                </button>
                {nonDcParts.map((p, i) => {
                    const label = p.split('=')[1] || p;
                    const isOu = /^OU=/i.test(p);
                    return (
                        <React.Fragment key={i}>
                            <span className="text-gray-600">/</span>
                            <button
                                type="button"
                                onClick={() => handleBreadcrumbClick(p, false)}
                                className="text-amber-400 flex items-center gap-1 hover:text-amber-300 hover:underline cursor-pointer transition-colors bg-transparent border-0 p-0"
                                title={`Navigate back to ${label}`}
                            >
                                <Folder size={12} className={isOu ? 'text-amber-400' : 'text-purple-400'} /> {label}
                            </button>
                        </React.Fragment>
                    );
                })}
                {name && (
                    <>
                        <span className="text-gray-600">/</span>
                        <span className="text-white font-bold">{name}</span>
                    </>
                )}
            </div>
        );
    };

    const selData = selectedObject?.data || domain || {};
    const selType = selectedObject?.type || 'domain';

    return (
        <div className="w-full h-[calc(100vh-12.5rem)] bg-gray-800/80 border border-gray-800 rounded-xl text-white flex flex-row overflow-hidden shadow-2xl">
            {/* Tree Sidebar */}
            <div className="w-80 bg-gray-900 border-r border-gray-800 flex flex-col shrink-0">
                <div className="p-2.5 border-b border-gray-800">
                    <div className="relative group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-orange-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search AD objects..."
                            className="w-full bg-gray-900 border border-gray-800 rounded-md pl-8 pr-3 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60 transition-all"
                        />
                    </div>
                </div>

                {/* Tree Items Scroll Container */}
                <div className="flex-1 overflow-y-auto custom-scroll p-2 space-y-1">
                    {/* Domain Root */}
                    <div
                        onClick={(e) => {
                            setSelectedObject({ type: 'domain', name: domain?.domain || 'Domain Root', data: domain });
                            toggleExpand('domain', e);
                        }}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-xs font-semibold ${
                            selType === 'domain' ? 'bg-orange-700 text-white border border-orange-500/30' : 'hover:bg-gray-900 text-orange-500'
                        }`}
                    >
                        <button onClick={(e) => { e.stopPropagation(); toggleExpand('domain', e); }} className="p-0.5 hover:bg-gray-800 rounded text-gray-200">
                            {isNodeExpanded('domain') ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                        <Globe size={14} className={` ${selType === 'domain' ? 'text-white':'text-orange-500'}shrink-0`} />
                        <span className="truncate">{domain?.domain || 'Active Directory Root'}</span>
                    </div>

                    {/* Expandable OUs & Built-in CN Containers under Domain Root */}
                    {isNodeExpanded('domain') && (
                        <div className="ml-3 pl-2 border-l border-gray-800 space-y-1">
                            {/* OUs */}
                            {filteredOus.map((ou) => {
                                const ouKey = `ou-${ou.object_guid}`;
                                const isOuExpanded = isNodeExpanded(ouKey, false);
                                const isOuSelected = selectedObject?.type === 'ou' && selectedObject?.data?.object_guid === ou.object_guid;
                                const cached = ouCache[ou.name];

                                const ouUsers = cached ? cached.users : initialUsers.filter(u => u.ou_name && u.ou_name.trim() !== "" ? u.ou_name.toLowerCase() === ou.name.toLowerCase() : (u.distinguished_name && ou.distinguished_name && u.distinguished_name.toLowerCase().includes(ou.distinguished_name.toLowerCase())));
                                const ouComputers = cached ? cached.computers : initialComputers.filter(c => c.ou_name && c.ou_name.trim() !== "" ? c.ou_name.toLowerCase() === ou.name.toLowerCase() : (c.distinguished_name && ou.distinguished_name && c.distinguished_name.toLowerCase().includes(ou.distinguished_name.toLowerCase())));
                                const ouGroups = cached ? cached.groups : initialGroups.filter(g => g.ou_name && g.ou_name.trim() !== "" ? g.ou_name.toLowerCase() === ou.name.toLowerCase() : (g.distinguished_name && ou.distinguished_name && g.distinguished_name.toLowerCase().includes(ou.distinguished_name.toLowerCase())));

                                const usersFolderKey = `${ouKey}-users`;
                                const computersFolderKey = `${ouKey}-computers`;
                                const groupsFolderKey = `${ouKey}-groups`;

                                const isUsersExpanded = isNodeExpanded(usersFolderKey, false);
                                const isComputersExpanded = isNodeExpanded(computersFolderKey, false);
                                const isGroupsExpanded = isNodeExpanded(groupsFolderKey, false);

                                const currentSearchTerm = searchTerms[usersFolderKey] || '';
                                const displayOuUsers = currentSearchTerm
                                    ? (searchResults[usersFolderKey] ?? ouUsers.filter(u => (u.display_name || u.name || u.sam_account_name || '').toLowerCase().includes(currentSearchTerm.toLowerCase())))
                                    : ouUsers;

                                const currentCompSearchTerm = searchTerms[computersFolderKey] || '';
                                const displayOuComputers = currentCompSearchTerm
                                    ? (searchResults[computersFolderKey] ?? ouComputers.filter(c => (c.name || c.sam_account_name || c.dns_host_name || '').toLowerCase().includes(currentCompSearchTerm.toLowerCase())))
                                    : ouComputers;

                                const currentGroupSearchTerm = searchTerms[groupsFolderKey] || '';
                                const displayOuGroups = currentGroupSearchTerm
                                    ? (searchResults[groupsFolderKey] ?? ouGroups.filter(g => (g.name || g.sam_account_name || g.common_name || '').toLowerCase().includes(currentGroupSearchTerm.toLowerCase())))
                                    : ouGroups;

                                const usersTotal = currentSearchTerm
                                    ? (folderTotals[usersFolderKey] ?? displayOuUsers.length)
                                    : (cached?.totalUsers ?? (ou.user_count !== undefined ? ou.user_count : ouUsers.length));

                                const compTotal = currentCompSearchTerm
                                    ? (folderTotals[computersFolderKey] ?? displayOuComputers.length)
                                    : (cached?.totalComputers ?? (ou.computer_count !== undefined ? ou.computer_count : ouComputers.length));

                                const groupTotal = currentGroupSearchTerm
                                    ? (folderTotals[groupsFolderKey] ?? displayOuGroups.length)
                                    : (cached?.totalGroups ?? (ou.group_count !== undefined ? ou.group_count : ouGroups.length));

                                return (
                                    <div key={ou.object_guid} className="space-y-1">
                                        <div
                                            onClick={(e) => handleSelectOU(ou, e)}
                                            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors text-xs ${
                                                isOuSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-300'
                                            }`}
                                        >
                                            <button onClick={(e) => handleToggleOU(ou, e)} className="p-0.5 hover:bg-gray-800 rounded text-gray-200">
                                                {isOuExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                            </button>
                                            {isOuExpanded ? (
                                                <FolderOpen size={13} className={isOuSelected ? 'text-white shrink-0' : 'text-amber-400 shrink-0'} />
                                            ) : (
                                                <Folder size={13} className={isOuSelected ? 'text-white shrink-0' : 'text-amber-400 shrink-0'} />
                                            )}
                                            <span className="truncate flex-1">{ou.name}</span>
                                        </div>

                                        {/* Sub-Folders under OU: Users, Computers, Groups */}
                                        {isOuExpanded && (
                                            <div className="ml-3 pl-2 border-l border-gray-800/60 space-y-1">
                                                {/* Users Sub-Folder */}
                                                <div className="space-y-0.5">
                                                    <div
                                                        onClick={(e) => {
                                                            toggleExpand(usersFolderKey, e);
                                                            if (!ouCache[ou.name]) loadOUDetails(ou);
                                                        }}
                                                        className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-900 text-emerald-400 text-[11px] font-semibold"
                                                    >
                                                        <button className="p-0.5 text-gray-200">
                                                            {isUsersExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                        </button>
                                                        <User size={13} className="text-emerald-400 shrink-0" />
                                                        <span className="truncate">Users ({usersTotal})</span>
                                                    </div>

                                                    {isUsersExpanded && (
                                                        <div
                                                            className="ml-3 pl-2 border-l border-gray-800/40 space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1"
                                                            onScroll={(e) => handleFolderScroll(e, usersFolderKey, ou.name, 'users', false)}
                                                        >
                                                            {/* User Search Input */}
                                                            <div className="px-1 py-0.5 sticky top-0 bg-gray-900 z-10" onClick={(e) => e.stopPropagation()}>
                                                                <div className="relative">
                                                                    <Search className="w-3 h-3 text-gray-200 absolute left-2 top-2" />
                                                                    <input
                                                                        type="text"
                                                                        value={currentSearchTerm}
                                                                        onChange={(e) => handleSearchObjects(usersFolderKey, ou.name, e.target.value, 'users', false)}
                                                                        placeholder="Search users..."
                                                                        className="w-full bg-gray-950/80 border border-gray-700/60 rounded px-2 py-1 pl-6 pr-6 text-[10px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                                                                    />
                                                                    {searchLoading[usersFolderKey] ? (
                                                                        <Loader2 className="w-3 h-3 text-orange-400 absolute right-2 top-1.5 animate-spin" />
                                                                    ) : currentSearchTerm ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSearchObjects(usersFolderKey, ou.name, '', 'users', false);
                                                                            }}
                                                                            className="absolute right-2 top-1 text-gray-200 hover:text-white text-xs font-bold"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            {displayOuUsers.length === 0 ? (
                                                                <span className="text-[10px] text-gray-600 font-mono italic px-2 block">
                                                                    {searchLoading[usersFolderKey] ? 'Searching...' : currentSearchTerm ? 'No matching users' : 'No users'}
                                                                </span>
                                                            ) : (
                                                                displayOuUsers.map(u => {
                                                                    const isSelected = selectedObject?.data?.object_guid === u.object_guid;
                                                                    return (
                                                                        <div
                                                                            key={u.object_guid}
                                                                            onClick={() => setSelectedObject({ type: 'user', name: u.display_name || u.name, data: u })}
                                                                            className={`flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer text-[11px] ml-2 ${
                                                                                isSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-200'
                                                                            }`}
                                                                        >
                                                                            <User size={11} className={isSelected ? 'text-white' : (u.domain_admin ? 'text-red-400' : 'text-emerald-400')} />
                                                                            <span className="truncate">{u.display_name || u.name}</span>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}

                                                            {loadingMore[usersFolderKey] && (
                                                                <div className="flex items-center justify-center py-1 text-[10px] text-orange-400 gap-1">
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                    <span>Loading more...</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Computers Sub-Folder */}
                                                <div className="space-y-0.5">
                                                    <div
                                                        onClick={(e) => {
                                                            toggleExpand(computersFolderKey, e);
                                                            if (!ouCache[ou.name]) loadOUDetails(ou);
                                                        }}
                                                        className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-900 text-purple-400 text-[11px] font-semibold"
                                                    >
                                                        <button className="p-0.5 text-gray-200">
                                                            {isComputersExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                        </button>
                                                        <Monitor size={13} className="text-purple-400 shrink-0" />
                                                        <span className="truncate">Computers ({compTotal})</span>
                                                    </div>

                                                    {isComputersExpanded && (
                                                        <div
                                                            className="ml-3 pl-2 border-l border-gray-800/40 space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1"
                                                            onScroll={(e) => handleFolderScroll(e, computersFolderKey, ou.name, 'computers', false)}
                                                        >
                                                            {/* Computer Search Input */}
                                                            <div className="px-1 py-0.5 sticky top-0 bg-gray-900 z-10" onClick={(e) => e.stopPropagation()}>
                                                                <div className="relative">
                                                                    <Search className="w-3 h-3 text-gray-200 absolute left-2 top-2" />
                                                                    <input
                                                                        type="text"
                                                                        value={currentCompSearchTerm}
                                                                        onChange={(e) => handleSearchObjects(computersFolderKey, ou.name, e.target.value, 'computers', false)}
                                                                        placeholder="Search computers..."
                                                                        className="w-full bg-gray-950/80 border border-gray-700/60 rounded px-2 py-1 pl-6 pr-6 text-[10px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                                                                    />
                                                                    {searchLoading[computersFolderKey] ? (
                                                                        <Loader2 className="w-3 h-3 text-orange-400 absolute right-2 top-1.5 animate-spin" />
                                                                    ) : currentCompSearchTerm ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSearchObjects(computersFolderKey, ou.name, '', 'computers', false);
                                                                            }}
                                                                            className="absolute right-2 top-1 text-gray-200 hover:text-white text-xs font-bold"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            {displayOuComputers.length === 0 ? (
                                                                <span className="text-[10px] text-gray-600 font-mono italic px-2 block">
                                                                    {searchLoading[computersFolderKey] ? 'Searching...' : currentCompSearchTerm ? 'No matching computers' : 'No computers'}
                                                                </span>
                                                            ) : (
                                                                displayOuComputers.map(c => {
                                                                    const isSelected = selectedObject?.data?.object_guid === c.object_guid;
                                                                    return (
                                                                        <div
                                                                            key={c.object_guid}
                                                                            onClick={() => setSelectedObject({ type: 'computer', name: c.name, data: c })}
                                                                            className={`flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer text-[11px] ml-2 ${
                                                                                isSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-200'
                                                                            }`}
                                                                        >
                                                                            <Monitor size={11} className={isSelected ? 'text-white' : 'text-purple-400'} />
                                                                            <span className="truncate">{c.name}</span>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}

                                                            {loadingMore[computersFolderKey] && (
                                                                <div className="flex items-center justify-center py-1 text-[10px] text-orange-400 gap-1">
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                    <span>Loading more...</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Groups Sub-Folder */}
                                                <div className="space-y-0.5">
                                                    <div
                                                        onClick={(e) => {
                                                            toggleExpand(groupsFolderKey, e);
                                                            if (!ouCache[ou.name]) loadOUDetails(ou);
                                                        }}
                                                        className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-900 text-indigo-400 text-[11px] font-semibold"
                                                    >
                                                        <button className="p-0.5 text-gray-200">
                                                            {isGroupsExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                        </button>
                                                        <Users size={13} className="text-indigo-400 shrink-0" />
                                                        <span className="truncate">Groups ({groupTotal})</span>
                                                    </div>

                                                    {isGroupsExpanded && (
                                                        <div
                                                            className="ml-3 pl-2 border-l border-gray-800/40 space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1"
                                                            onScroll={(e) => handleFolderScroll(e, groupsFolderKey, ou.name, 'groups', false)}
                                                        >
                                                            {/* Group Search Input */}
                                                            <div className="px-1 py-0.5 sticky top-0 bg-gray-900 z-10" onClick={(e) => e.stopPropagation()}>
                                                                <div className="relative">
                                                                    <Search className="w-3 h-3 text-gray-200 absolute left-2 top-2" />
                                                                    <input
                                                                        type="text"
                                                                        value={currentGroupSearchTerm}
                                                                        onChange={(e) => handleSearchObjects(groupsFolderKey, ou.name, e.target.value, 'groups', false)}
                                                                        placeholder="Search groups..."
                                                                        className="w-full bg-gray-950/80 border border-gray-700/60 rounded px-2 py-1 pl-6 pr-6 text-[10px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                                                                    />
                                                                    {searchLoading[groupsFolderKey] ? (
                                                                        <Loader2 className="w-3 h-3 text-orange-400 absolute right-2 top-1.5 animate-spin" />
                                                                    ) : currentGroupSearchTerm ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSearchObjects(groupsFolderKey, ou.name, '', 'groups', false);
                                                                            }}
                                                                            className="absolute right-2 top-1 text-gray-200 hover:text-white text-xs font-bold"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            {displayOuGroups.length === 0 ? (
                                                                <span className="text-[10px] text-gray-600 font-mono italic px-2 block">
                                                                    {searchLoading[groupsFolderKey] ? 'Searching...' : currentGroupSearchTerm ? 'No matching groups' : 'No groups'}
                                                                </span>
                                                            ) : (
                                                                displayOuGroups.map(g => {
                                                                    const isSelected = selectedObject?.data?.object_guid === g.object_guid;
                                                                    return (
                                                                        <div
                                                                            key={g.object_guid}
                                                                            onClick={() => setSelectedObject({ type: 'group', name: g.name, data: g })}
                                                                            className={`flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer text-[11px] ml-2 ${
                                                                                isSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-200'
                                                                            }`}
                                                                        >
                                                                            <Users size={11} className={isSelected ? 'text-white' : 'text-indigo-400'} />
                                                                            <span className="truncate">{g.name}</span>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}

                                                            {loadingMore[groupsFolderKey] && (
                                                                <div className="flex items-center justify-center py-1 text-[10px] text-orange-400 gap-1">
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                    <span>Loading more...</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Built-in CN Containers (Users, Computers, Builtin) */}
                            {cnContainers.map((c) => {
                                const cKey = `cn-${c.name}`;
                                const isContainerExpanded = isNodeExpanded(cKey, false);
                                const isContainerSelected = selectedObject?.type === 'container' && (selectedObject?.data?.name === c.name || selectedObject?.name === `CN=${c.name}` || selectedObject?.name === c.name);

                                // const cNameLower = c.name.toLowerCase();
                                const cachedC = ouCache[cKey];

                                const allUsers = cachedC ? [...initialUsers, ...cachedC.users] : initialUsers;
                                const allComputers = cachedC ? [...initialComputers, ...cachedC.computers] : initialComputers;
                                const allGroups = cachedC ? [...initialGroups, ...cachedC.groups] : initialGroups;

                                const uList = allUsers.filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);
                                const cList = allComputers.filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);
                                const gList = allGroups.filter((v, i, a) => a.findIndex(x => x.object_guid === v.object_guid) === i);

                                const cUsers = uList.filter(u => matchContainerObject(u, c.name));
                                const cComputers = cList.filter(comp => matchContainerObject(comp, c.name));
                                const cGroups = gList.filter(g => matchContainerObject(g, c.name));

                                const usersFolderKey = `${cKey}-users`;
                                const computersFolderKey = `${cKey}-computers`;
                                const groupsFolderKey = `${cKey}-groups`;

                                const isUsersExpanded = isNodeExpanded(usersFolderKey, false);
                                const isComputersExpanded = isNodeExpanded(computersFolderKey, false);
                                const isGroupsExpanded = isNodeExpanded(groupsFolderKey, false);

                                const currentSearchTerm = searchTerms[usersFolderKey] || '';
                                const displayCUsers = currentSearchTerm
                                    ? (searchResults[usersFolderKey] ?? cUsers.filter(u => (u.display_name || u.name || u.sam_account_name || '').toLowerCase().includes(currentSearchTerm.toLowerCase())))
                                    : cUsers;

                                const currentCompSearchTerm = searchTerms[computersFolderKey] || '';
                                const displayCComputers = currentCompSearchTerm
                                    ? (searchResults[computersFolderKey] ?? cComputers.filter(c => (c.name || c.sam_account_name || c.dns_host_name || '').toLowerCase().includes(currentCompSearchTerm.toLowerCase())))
                                    : cComputers;

                                const currentGroupSearchTerm = searchTerms[groupsFolderKey] || '';
                                const displayCGroups = currentGroupSearchTerm
                                    ? (searchResults[groupsFolderKey] ?? cGroups.filter(g => (g.name || g.sam_account_name || g.common_name || '').toLowerCase().includes(currentGroupSearchTerm.toLowerCase())))
                                    : cGroups;

                                const cUsersTotal = currentSearchTerm
                                    ? (folderTotals[usersFolderKey] ?? displayCUsers.length)
                                    : (cachedC?.totalUsers ?? (c.user_count !== undefined ? c.user_count : cUsers.length));

                                const cCompTotal = currentCompSearchTerm
                                    ? (folderTotals[computersFolderKey] ?? displayCComputers.length)
                                    : (cachedC?.totalComputers ?? (c.computer_count !== undefined ? c.computer_count : cComputers.length));

                                const cGroupTotal = currentGroupSearchTerm
                                    ? (folderTotals[groupsFolderKey] ?? displayCGroups.length)
                                    : (cachedC?.totalGroups ?? (c.group_count !== undefined ? c.group_count : cGroups.length));

                                return (
                                    <div key={cKey} className="space-y-1">
                                        <div
                                            onClick={(e) => handleSelectContainer(c, e)}
                                            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors text-xs ${
                                                isContainerSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-300'
                                            }`}
                                        >
                                            <button onClick={(e) => handleToggleContainer(c, e)} className="p-0.5 hover:bg-gray-800 rounded text-gray-200">
                                                {isContainerExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                            </button>
                                            {isContainerExpanded ? (
                                                <FolderOpen size={13} className={isContainerSelected ? 'text-white shrink-0' : 'text-purple-400 shrink-0'} />
                                            ) : (
                                                <Folder size={13} className={isContainerSelected ? 'text-white shrink-0' : 'text-purple-400 shrink-0'} />
                                            )}
                                            <span className="truncate flex-1">CN={c.name}</span>
                                        </div>

                                        {/* Sub-Folders under CN Container */}
                                        {isContainerExpanded && (
                                            <div className="ml-3 pl-2 border-l border-gray-800/60 space-y-1">
                                                {/* Users Sub-Folder */}
                                                {(c.name === 'Users' || cUsers.length > 0 || displayCUsers.length > 0) && (
                                                    <div className="space-y-0.5">
                                                        <div
                                                            onClick={(e) => {
                                                                toggleExpand(usersFolderKey, e);
                                                                if (!ouCache[cKey]) loadContainerDetails(c.name);
                                                            }}
                                                            className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-900 text-emerald-400 text-[11px] font-semibold"
                                                        >
                                                            <button className="p-0.5 text-gray-200">
                                                                {isUsersExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                            </button>
                                                            <User size={13} className="text-emerald-400 shrink-0" />
                                                            <span className="truncate">Users ({cUsersTotal})</span>
                                                        </div>

                                                        {isUsersExpanded && (
                                                            <div
                                                                className="ml-3 pl-2 border-l border-gray-800/40 space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1"
                                                                onScroll={(e) => handleFolderScroll(e, usersFolderKey, c.name, 'users', true)}
                                                            >
                                                                {/* User Search Input */}
                                                                <div className="px-1 py-0.5 sticky top-0 bg-gray-900 z-10" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="relative">
                                                                        <Search className="w-3 h-3 text-gray-200 absolute left-2 top-2" />
                                                                        <input
                                                                            type="text"
                                                                            value={currentSearchTerm}
                                                                            onChange={(e) => handleSearchObjects(usersFolderKey, c.name, e.target.value, 'users', true)}
                                                                            placeholder="Search users..."
                                                                            className="w-full bg-gray-950/80 border border-gray-700/60 rounded px-2 py-1 pl-6 pr-6 text-[10px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                                                                        />
                                                                        {searchLoading[usersFolderKey] ? (
                                                                            <Loader2 className="w-3 h-3 text-orange-400 absolute right-2 top-1.5 animate-spin" />
                                                                        ) : currentSearchTerm ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSearchObjects(usersFolderKey, c.name, '', 'users', true);
                                                                                }}
                                                                                className="absolute right-2 top-1 text-gray-200 hover:text-white text-xs font-bold"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        ) : null}
                                                                    </div>
                                                                </div>

                                                                {displayCUsers.length === 0 ? (
                                                                    <span className="text-[10px] text-gray-600 font-mono italic px-2 block">
                                                                        {searchLoading[usersFolderKey] ? 'Searching...' : currentSearchTerm ? 'No matching users' : 'No users'}
                                                                    </span>
                                                                ) : (
                                                                    displayCUsers.map(u => {
                                                                        const isSelected = selectedObject?.data?.object_guid === u.object_guid;
                                                                        return (
                                                                            <div
                                                                                key={u.object_guid}
                                                                                onClick={() => setSelectedObject({ type: 'user', name: u.display_name || u.name, data: u })}
                                                                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer text-[11px] ml-2 ${
                                                                                    isSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-200'
                                                                                }`}
                                                                            >
                                                                                <User size={11} className={isSelected ? 'text-white' : (u.domain_admin ? 'text-red-400' : 'text-emerald-400')} />
                                                                                <span className="truncate">{u.display_name || u.name}</span>
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}

                                                                {loadingMore[usersFolderKey] && (
                                                                    <div className="flex items-center justify-center py-1 text-[10px] text-orange-400 gap-1">
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                        <span>Loading more...</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Computers Sub-Folder */}
                                                {(c.name === 'Computers' || cComputers.length > 0 || displayCComputers.length > 0) && (
                                                    <div className="space-y-0.5">
                                                        <div
                                                            onClick={(e) => {
                                                                toggleExpand(computersFolderKey, e);
                                                                if (!ouCache[cKey]) loadContainerDetails(c.name);
                                                            }}
                                                            className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-900 text-purple-400 text-[11px] font-semibold"
                                                        >
                                                            <button className="p-0.5 text-gray-200">
                                                                {isComputersExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                            </button>
                                                            <Monitor size={13} className="text-purple-400 shrink-0" />
                                                            <span className="truncate">Computers ({cCompTotal})</span>
                                                        </div>

                                                        {isComputersExpanded && (
                                                            <div
                                                                className="ml-3 pl-2 border-l border-gray-800/40 space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1"
                                                                onScroll={(e) => handleFolderScroll(e, computersFolderKey, c.name, 'computers', true)}
                                                            >
                                                                {/* Computer Search Input */}
                                                                <div className="px-1 py-0.5 sticky top-0 bg-gray-900 z-10" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="relative">
                                                                        <Search className="w-3 h-3 text-gray-200 absolute left-2 top-2" />
                                                                        <input
                                                                            type="text"
                                                                            value={currentCompSearchTerm}
                                                                            onChange={(e) => handleSearchObjects(computersFolderKey, c.name, e.target.value, 'computers', true)}
                                                                            placeholder="Search computers..."
                                                                            className="w-full bg-gray-950/80 border border-gray-700/60 rounded px-2 py-1 pl-6 pr-6 text-[10px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                                                                        />
                                                                        {searchLoading[computersFolderKey] ? (
                                                                            <Loader2 className="w-3 h-3 text-orange-400 absolute right-2 top-1.5 animate-spin" />
                                                                        ) : currentCompSearchTerm ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSearchObjects(computersFolderKey, c.name, '', 'computers', true);
                                                                                }}
                                                                                className="absolute right-2 top-1 text-gray-200 hover:text-white text-xs font-bold"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        ) : null}
                                                                    </div>
                                                                </div>

                                                                {displayCComputers.length === 0 ? (
                                                                    <span className="text-[10px] text-gray-600 font-mono italic px-2 block">
                                                                        {searchLoading[computersFolderKey] ? 'Searching...' : currentCompSearchTerm ? 'No matching computers' : 'No computers'}
                                                                    </span>
                                                                ) : (
                                                                    displayCComputers.map(comp => {
                                                                        const isSelected = selectedObject?.data?.object_guid === comp.object_guid;
                                                                        return (
                                                                            <div
                                                                                key={comp.object_guid}
                                                                                onClick={() => setSelectedObject({ type: 'computer', name: comp.name, data: comp })}
                                                                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer text-[11px] ml-2 ${
                                                                                    isSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-200'
                                                                                }`}
                                                                            >
                                                                                <Monitor size={11} className={isSelected ? 'text-white' : 'text-purple-400'} />
                                                                                <span className="truncate">{comp.name}</span>
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}

                                                                {loadingMore[computersFolderKey] && (
                                                                    <div className="flex items-center justify-center py-1 text-[10px] text-orange-400 gap-1">
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                        <span>Loading more...</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Groups Sub-Folder */}
                                                {(c.name === 'Builtin' || cGroups.length > 0 || displayCGroups.length > 0) && (
                                                    <div className="space-y-0.5">
                                                        <div
                                                            onClick={(e) => {
                                                                toggleExpand(groupsFolderKey, e);
                                                                if (!ouCache[cKey]) loadContainerDetails(c.name);
                                                            }}
                                                            className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-900 text-indigo-400 text-[11px] font-semibold"
                                                        >
                                                            <button className="p-0.5 text-gray-200">
                                                                {isGroupsExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                                            </button>
                                                            <Users size={13} className="text-indigo-400 shrink-0" />
                                                            <span className="truncate">Groups ({cGroupTotal})</span>
                                                        </div>

                                                        {isGroupsExpanded && (
                                                            <div
                                                                className="ml-3 pl-2 border-l border-gray-800/40 space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1"
                                                                onScroll={(e) => handleFolderScroll(e, groupsFolderKey, c.name, 'groups', true)}
                                                            >
                                                                {/* Group Search Input */}
                                                                <div className="px-1 py-0.5 sticky top-0 bg-gray-900 z-10" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="relative">
                                                                        <Search className="w-3 h-3 text-gray-200 absolute left-2 top-2" />
                                                                        <input
                                                                            type="text"
                                                                            value={currentGroupSearchTerm}
                                                                            onChange={(e) => handleSearchObjects(groupsFolderKey, c.name, e.target.value, 'groups', true)}
                                                                            placeholder="Search groups..."
                                                                            className="w-full bg-gray-950/80 border border-gray-700/60 rounded px-2 py-1 pl-6 pr-6 text-[10px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                                                                        />
                                                                        {searchLoading[groupsFolderKey] ? (
                                                                            <Loader2 className="w-3 h-3 text-orange-400 absolute right-2 top-1.5 animate-spin" />
                                                                        ) : currentGroupSearchTerm ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSearchObjects(groupsFolderKey, c.name, '', 'groups', true);
                                                                                }}
                                                                                className="absolute right-2 top-1 text-gray-200 hover:text-white text-xs font-bold"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        ) : null}
                                                                    </div>
                                                                </div>

                                                                {displayCGroups.length === 0 ? (
                                                                    <span className="text-[10px] text-gray-600 font-mono italic px-2 block">
                                                                        {searchLoading[groupsFolderKey] ? 'Searching...' : currentGroupSearchTerm ? 'No matching groups' : 'No groups'}
                                                                    </span>
                                                                ) : (
                                                                    displayCGroups.map(g => {
                                                                        const isSelected = selectedObject?.data?.object_guid === g.object_guid;
                                                                        return (
                                                                            <div
                                                                                key={g.object_guid}
                                                                                onClick={() => setSelectedObject({ type: 'group', name: g.name, data: g })}
                                                                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded cursor-pointer text-[11px] ${
                                                                                    isSelected ? 'bg-orange-700 text-white border border-orange-500/30 font-bold' : 'hover:bg-gray-900 text-gray-200'
                                                                                }`}
                                                                            >
                                                                                <Users size={11} className={isSelected ? 'text-white' : 'text-indigo-400'} />
                                                                                <span className="truncate">{g.name}</span>
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}

                                                                {loadingMore[groupsFolderKey] && (
                                                                    <div className="flex items-center justify-center py-1 text-[10px] text-orange-400 gap-1">
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                        <span>Loading more...</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Detail Panel */}
            <div className="flex-1 overflow-y-auto custom-scroll p-4 bg-gray-900/40 space-y-2">
                {/* Detail Card Header */}
                <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-lg border flex items-center justify-center ${
                                selType === 'domain' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' :
                                selType === 'ou' ? 'bg-amber-500/20 border-amber-500/30 text-amber-400' :
                                selType === 'container' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' :
                                selType === 'computer' ? 'bg-purple-500/20 border-purple-500/30 text-purple-400' :
                                selType === 'group' ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400' :
                                selData?.domain_admin ? 'bg-red-500/20 border-red-500/30 text-red-400' :
                                'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                            }`}>
                                {selType === 'domain' ? <Globe size={20} /> :
                                 selType === 'ou' || selType === 'container' ? <Folder size={20} /> :
                                 selType === 'computer' ? <Monitor size={20} /> :
                                 selType === 'group' ? <Users size={20} /> :
                                 selData?.domain_admin ? <ShieldAlert size={20} /> : <User size={20} />}
                            </div>

                            <div>
                                <h2 className="text-base font-bold text-white tracking-wide">{selectedObject.name || selData.domain || 'Domain'}</h2>
                                <span className="text-[10px] text-gray-200 uppercase tracking-widest font-mono font-bold">
                                    TYPE: {selType.toUpperCase()}
                                </span>
                            </div>
                        </div>

                        {loadingOu && (
                            <div className="flex items-center gap-1.5 text-xs text-orange-400 font-mono">
                                <Loader2 size={13} className="animate-spin" /> Fetching OU details...
                            </div>
                        )}
                    </div>

                    {renderBreadcrumb(selData.distinguished_name, selectedObject.name)}
                </div>

                {/* If DOMAIN: Render Comprehensive Domain Overview Cards & Metrics */}
                {selType === 'domain' && selData && (
                    <div className="space-y-2">
                        {/* Domain System & Network Identity */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Globe className="text-orange-500 w-4 h-4" />
                                Domain System & Network Identity
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 text-xs font-mono">
                                {selData.distinguished_name && (
                                    <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800 col-span-full">
                                        <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Distinguished Name</label>
                                        <span className="text-gray-200 break-all">{selData.distinguished_name}</span>
                                    </div>
                                )}
                                {selData.dns_host_name && (
                                    <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                        <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">DNS Host Name</label>
                                        <span className="text-blue-300 font-bold">{selData.dns_host_name}</span>
                                    </div>
                                )}
                                {selData.operating_system && (
                                    <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800 col-span-2">
                                        <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Operating System</label>
                                        <span className="text-amber-300 font-bold">
                                            {selData.operating_system} {selData.operating_system_version ? `(${selData.operating_system_version})` : ''}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Users & Security Accounts Grid */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <User className="text-orange-500 w-4 h-4" />
                                Users & Security Accounts
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Total Users</span>
                                    <span className="text-base font-bold text-emerald-400 font-mono mt-0.5">{selData.user_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Domain Admins</span>
                                    <span className="text-base font-bold text-red-400 font-mono mt-0.5">{selData.domain_admin_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Enterprise Admins</span>
                                    <span className="text-base font-bold text-red-300 font-mono mt-0.5">{selData.enterprise_admin_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Disabled Users</span>
                                    <span className="text-base font-bold text-gray-200 font-mono mt-0.5">{selData.disabled_user_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Locked Users</span>
                                    <span className="text-base font-bold text-amber-400 font-mono mt-0.5">{selData.locked_user_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Pwd Never Expires</span>
                                    <span className="text-base font-bold text-purple-400 font-mono mt-0.5">{selData.pwd_never_expires_count ?? 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* Computers & Organizational Structure Grid */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Monitor className="text-orange-500 w-4 h-4" />
                                Infrastructure & Groups Breakdown
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5">
                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Total Computers</span>
                                    <span className="text-base font-bold text-purple-400 font-mono mt-0.5">{selData.computer_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Disabled Computers</span>
                                    <span className="text-base font-bold text-gray-200 font-mono mt-0.5">{selData.disabled_computer_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Total OUs</span>
                                    <span className="text-base font-bold text-amber-400 font-mono mt-0.5">{selData.ou_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Empty OUs</span>
                                    <span className="text-base font-bold text-gray-200 font-mono mt-0.5">{selData.empty_ou_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Total Groups</span>
                                    <span className="text-base font-bold text-indigo-400 font-mono mt-0.5">{selData.group_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Built-in Groups</span>
                                    <span className="text-base font-bold text-indigo-300 font-mono mt-0.5">{selData.builtin_group_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Custom Groups</span>
                                    <span className="text-base font-bold text-blue-400 font-mono mt-0.5">{selData.custom_group_count ?? 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* If USER: Render Comprehensive User Details & Account Status Grid */}
                {selType === 'user' && selData && (
                    <div className="space-y-2">
                        {/* Security & Account Status Badges */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Shield className="text-orange-500 w-4 h-4" />
                                Account Status & Privilege Flags
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Account Status</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.account_disabled ? 'text-gray-200' : 'text-emerald-400'}`}>
                                        {selData.account_disabled ? 'Disabled' : 'Enabled'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Lockout</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.lockout ? 'text-red-400' : 'text-emerald-400'}`}>
                                        {selData.lockout ? 'Locked Out' : 'Unlocked'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Password Expired</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.password_expired ? 'text-amber-400' : 'text-emerald-400'}`}>
                                        {selData.password_expired ? 'Expired' : 'Valid'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Pwd Never Expires</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.password_never_expires ? 'text-purple-400' : 'text-gray-300'}`}>
                                        {selData.password_never_expires ? 'Yes' : 'No'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Pwd Not Required</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.password_not_required ? 'text-red-400' : 'text-gray-300'}`}>
                                        {selData.password_not_required ? 'Yes' : 'No'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Cannot Change Pwd</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.cannot_change_password ? 'text-amber-300' : 'text-gray-300'}`}>
                                        {selData.cannot_change_password ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* User Key Attributes & Timestamps */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <User className="text-orange-500 w-4 h-4" />
                                Account Attributes & Logon Statistics
                            </h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs font-mono">
                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">SAM Account Name</label>
                                    <span className="text-white font-bold">{selData.sam_account_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">UPN</label>
                                    <span className="text-blue-300 font-bold break-all">{selData.user_principal_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">OU Name</label>
                                    <span className="text-amber-300 font-bold">{selData.ou_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Domain</label>
                                    <span className="text-teal-300 font-bold">{selData.domain || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Created When</label>
                                    <span className="text-gray-200">{selData.created_when || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Modified When</label>
                                    <span className="text-gray-200">{selData.modified_when || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Account Expire Date</label>
                                    <span className="text-gray-200">{selData.account_expire_date || 'Never Expires'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Password Expiry Date</label>
                                    <span className="text-amber-300 font-bold">{selData.password_expiry_date || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Logon Count</label>
                                    <span className="text-emerald-400 font-bold">{selData.logon_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Bad Logon Count</label>
                                    <span className="text-red-400 font-bold">{selData.bad_logon_count ?? 0}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Last Logon Date</label>
                                    <span className="text-gray-200">{selData.last_logon_date || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">PWD Last Set Time</label>
                                    <span className="text-gray-200">{selData.pwd_last_set_time || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Group Memberships */}
                        {selData.member_of && (
                            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-2">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                    <Users className="text-orange-500 w-4 h-4" />
                                    Member Of Groups
                                </h3>
                                <div className="bg-gray-800 p-3 rounded-md border border-gray-800 text-xs font-mono text-indigo-300 break-all leading-relaxed">
                                    {Array.isArray(selData.member_of)
                                        ? selData.member_of.join(', ')
                                        : String(selData.member_of)}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* If GROUP: Render Comprehensive Group Details & Members List */}
                {selType === 'group' && selData && (
                    <div className="space-y-2">
                        {/* Group Security & Classification Flags */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Users className="text-orange-500 w-4 h-4" />
                                Group Classification & Security Flags
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Category</span>
                                    <span className="text-xs font-bold text-indigo-400 font-mono mt-1">
                                        {selData.category || 'Security'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Scope</span>
                                    <span className="text-xs font-bold text-blue-400 font-mono mt-1">
                                        {selData.scope || 'Global'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">System Critical</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.is_critical_system_object ? 'text-red-400' : 'text-gray-300'}`}>
                                        {selData.is_critical_system_object ? 'Critical System' : 'Standard'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Membership Status</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.empty ? 'text-gray-200' : 'text-emerald-400'}`}>
                                        {selData.empty ? 'Empty Group' : 'Has Members'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Group Type Code</span>
                                    <span className="text-xs font-mono text-purple-300 mt-1">
                                        {selData.group_type || '-'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Group Identity & Metadata Grid */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Hash className="text-orange-500 w-4 h-4" />
                                Group Metadata & Identity Attributes
                            </h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs font-mono">
                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">SAM Account Name</label>
                                    <span className="text-white font-bold">{selData.sam_account_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Common Name</label>
                                    <span className="text-blue-300 font-bold">{selData.common_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">OU Name</label>
                                    <span className="text-amber-300 font-bold">{selData.ou_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Domain</label>
                                    <span className="text-teal-300 font-bold">{selData.domain || '-'}</span>
                                </div>

                                {selData.cn && (
                                    <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800 col-span-2">
                                        <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Canonical Path (CN)</label>
                                        <span className="text-gray-200 break-all">{selData.cn}</span>
                                    </div>
                                )}

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Group Created</label>
                                    <span className="text-gray-200">{selData.group_created || selData.created_when || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Modified When</label>
                                    <span className="text-gray-200">{selData.modified_when || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Managed By</label>
                                    <span className="text-gray-200">{selData.managed_by || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800 col-span-3">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Description</label>
                                    <span className="text-gray-200">{selData.description || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Group Members List */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <User className="text-orange-500 w-4 h-4" />
                                Group Members ({Array.isArray(selData.member) ? selData.member.length : 0})
                            </h3>

                            {Array.isArray(selData.member) && selData.member.length > 0 ? (
                                <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scroll">
                                    {selData.member.map((m: any, mIdx: number) => {
                                        const mStr = typeof m === 'string' ? m : m.name || m.distinguished_name || String(m);
                                        const displayCn = mStr.split(',')[0].replace(/^CN=/i, '');
                                        return (
                                            <div key={mIdx} className="bg-gray-800 p-2 rounded border border-gray-800 text-xs font-mono text-emerald-300 flex items-center gap-2">
                                                <User size={12} className="text-emerald-400 shrink-0" />
                                                <span className="truncate" title={mStr}>{displayCn}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="bg-gray-800 p-3 rounded-md border border-gray-800 text-xs font-mono text-gray-500 italic text-center">
                                    No members in this group ({selData.empty ? 'Empty Group' : '0 Members'}).
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* If COMPUTER: Render Comprehensive Computer Details */}
                {selType === 'computer' && selData && (
                    <div className="space-y-2">
                        {/* Computer Security & Status Badges */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Shield className="text-orange-500 w-4 h-4" />
                                Computer Status & Security Flags
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Account Status</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.account_disabled ? 'text-gray-200' : 'text-emerald-400'}`}>
                                        {selData.account_disabled ? 'Disabled' : 'Enabled'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Lockout</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.locked_out ? 'text-red-400' : 'text-emerald-400'}`}>
                                        {selData.locked_out ? 'Locked Out' : 'Unlocked'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Critical System</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.is_critical_system_object ? 'text-red-400' : 'text-gray-300'}`}>
                                        {selData.is_critical_system_object ? 'Critical Asset' : 'Standard'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Password Expired</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.password_expired ? 'text-amber-400' : 'text-emerald-400'}`}>
                                        {selData.password_expired ? 'Expired' : 'Valid'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Pwd Never Expires</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.password_never_expires ? 'text-purple-400' : 'text-gray-300'}`}>
                                        {selData.password_never_expires ? 'Yes' : 'No'}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-lg border border-gray-800 flex flex-col">
                                    <span className="text-[9px] text-gray-200 uppercase font-bold">Pwd Not Required</span>
                                    <span className={`text-xs font-bold font-mono mt-1 ${selData.password_not_required ? 'text-red-400' : 'text-gray-300'}`}>
                                        {selData.password_not_required ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Computer System Attributes & OS Info */}
                        <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <Monitor className="text-orange-500 w-4 h-4" />
                                Computer Identity & Operating System
                            </h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs font-mono">
                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Computer Name</label>
                                    <span className="text-white font-bold">{selData.name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">SAM Account Name</label>
                                    <span className="text-purple-300 font-bold">{selData.sam_account_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">DNS Host Name</label>
                                    <span className="text-blue-300 font-bold">{selData.dns_host_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Operating System</label>
                                    <span className="text-amber-300 font-bold">
                                        {selData.operating_system || '-'} {selData.operating_system_version && selData.operating_system_version !== '-' ? `(${selData.operating_system_version})` : ''}
                                    </span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">OU Name</label>
                                    <span className="text-amber-300 font-bold">{selData.ou_name || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Domain</label>
                                    <span className="text-teal-300 font-bold">{selData.domain || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Created When</label>
                                    <span className="text-gray-200">{selData.created_when || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Modified When</label>
                                    <span className="text-gray-200">{selData.modified_when || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">PWD Last Set Time</label>
                                    <span className="text-gray-200">{selData.pwd_last_set_time || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Last Logon Date</label>
                                    <span className="text-gray-200">{selData.last_logon_date || '-'}</span>
                                </div>

                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800 col-span-2">
                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Last Logon Timestamp (PowerShell)</label>
                                    <span className="text-gray-200">{selData.last_logon_timestamp_pshell || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Group Memberships if present */}
                        {selData.member_of && selData.member_of !== '-' && (
                            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-2">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                    <Users className="text-indigo-400 w-4 h-4" />
                                    Member Of Groups
                                </h3>
                                <div className="bg-gray-800 p-3 rounded-md border border-gray-800 text-xs font-mono text-indigo-300 break-all leading-relaxed">
                                    {Array.isArray(selData.member_of)
                                        ? selData.member_of.join(', ')
                                        : String(selData.member_of)}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Direct Children Table ONLY IF OU or Container is selected */}
                {(selType === 'ou' || selType === 'container') && (() => {
                    const selOuName = selData.name || selectedObject.name || '';
                    const cleanContainerName = selOuName.replace(/^CN=/i, '');
                    const selCached = selType === 'ou' ? ouCache[selOuName] : (ouCache[`cn-${cleanContainerName}`] || ouCache[cleanContainerName]);
                    const selTotalUsers = selCached?.totalUsers ?? selData?.user_count ?? currentChildren.filter((x: any) => x._kind === 'user').length;
                    const selTotalComputers = selCached?.totalComputers ?? selData?.computer_count ?? currentChildren.filter((x: any) => x._kind === 'computer').length;
                    const selTotalGroups = selCached?.totalGroups ?? selData?.group_count ?? currentChildren.filter((x: any) => x._kind === 'group').length;
                    const selTotalChildren = (selCached?.totalUsers !== undefined || selCached?.totalComputers !== undefined || selCached?.totalGroups !== undefined)
                        ? (selTotalUsers + selTotalComputers + selTotalGroups)
                        : currentChildren.length;

                    return (
                        <div className="space-y-2">
                            {/* OU / Container Metadata Card */}
                            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                    <Folder className="text-orange-400 w-4 h-4" />
                                    {selType === 'ou' ? 'Organizational Unit Attributes' : 'Built-in System Container Details'}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs font-mono">
                                    {selType === 'container' ? (
                                        <>
                                            <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Container Name</label>
                                                <span className="text-orange-300 font-bold">{selectedObject.name}</span>
                                            </div>
                                            <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Users Count</label>
                                                <span className="text-emerald-300 font-bold">{selTotalUsers}</span>
                                            </div>
                                            <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Computers Count</label>
                                                <span className="text-purple-300 font-bold">{selTotalComputers}</span>
                                            </div>
                                            <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Groups Count</label>
                                                <span className="text-indigo-300 font-bold">{selTotalGroups}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {selData.distinguished_name && (
                                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800 col-span-full">
                                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Distinguished Name</label>
                                                    <span className="text-gray-200 break-all">{selData.distinguished_name}</span>
                                                </div>
                                            )}
                                            {selData.object_guid && (
                                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Object GUID</label>
                                                    <span className="text-cyan-300 font-bold">{selData.object_guid}</span>
                                                </div>
                                            )}
                                            {selData.created && (
                                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Created</label>
                                                    <span className="text-gray-200">{selData.created.split('T')[0]}</span>
                                                </div>
                                            )}
                                            {selData.updated && (
                                                <div className="bg-gray-800 p-2.5 rounded-md border border-gray-800">
                                                    <label className="text-[8px] text-gray-200 uppercase font-bold block mb-0.5">Updated</label>
                                                    <span className="text-gray-200">{selData.updated.split('T')[0]}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Direct Children Table */}
                            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <div className='flex items-center gap-2'>
                                    <List className='w-4 h-4 text-orange-400'/>
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                                    Direct Children Objects ({currentChildren.length < selTotalChildren ? `${currentChildren.length} of ${selTotalChildren}` : selTotalChildren} objects)
                                </h3>
                                </div>
                            </div>

                        <div
                            className="overflow-x-auto max-h-[420px] overflow-y-auto custom-scroll"
                            onScroll={(e) => {
                                if (selectedObject?.type === 'ou' && selectedObject?.data?.name) {
                                    const ouName = selectedObject.data.name;
                                    const ouKey = `ou-${selectedObject.data.object_guid}`;
                                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                                    if (scrollHeight - scrollTop - clientHeight < 50) {
                                        loadMoreItems(`${ouKey}-users`, ouName, 'users', false);
                                        loadMoreItems(`${ouKey}-computers`, ouName, 'computers', false);
                                        loadMoreItems(`${ouKey}-groups`, ouName, 'groups', false);
                                    }
                                } else if (selectedObject?.type === 'container' && (selectedObject?.data?.name || selectedObject?.name)) {
                                    const cName = selectedObject.data?.name || selectedObject.name.replace(/^CN=/i, '');
                                    const cKey = `cn-${cName}`;
                                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                                    if (scrollHeight - scrollTop - clientHeight < 50) {
                                        loadMoreItems(`${cKey}-users`, cName, 'users', true);
                                        loadMoreItems(`${cKey}-computers`, cName, 'computers', true);
                                        loadMoreItems(`${cKey}-groups`, cName, 'groups', true);
                                    }
                                }
                            }}
                        >
                            <table className="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-800 bg-gray-800 text-gray-200 uppercase text-[9px] tracking-widest font-bold sticky top-0 z-10">
                                        <th className="p-2.5">Name</th>
                                        <th className="p-2.5">Type</th>
                                        <th className="p-2.5">SAM / Hostname</th>
                                        <th className="p-2.5">Distinguished Name</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                    {currentChildren.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="py-8 text-center text-gray-200 font-mono text-xs">
                                                No child objects found in this {selType.toUpperCase()}.
                                            </td>
                                        </tr>
                                    ) : (
                                        currentChildren.map((item: any, idx: number) => {
                                            const kind = item._kind || 'user';
                                            const isUser = kind === 'user';
                                            const isComp = kind === 'computer';

                                            const icon = isUser ? (
                                                <User size={13} className={item.domain_admin ? 'text-red-400' : 'text-emerald-400'} />
                                            ) : isComp ? (
                                                <Monitor size={13} className="text-purple-400" />
                                            ) : (
                                                <Users size={13} className="text-indigo-400" />
                                            );

                                            const badgeCls = isUser
                                                ? item.domain_admin ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'
                                                : isComp
                                                ? 'bg-purple-500/20 text-purple-300'
                                                : 'bg-indigo-500/20 text-indigo-300';

                                            const typeLabel = isUser ? (item.domain_admin ? 'Admin User' : 'User') : isComp ? 'Computer' : 'Group';

                                            return (
                                                <tr
                                                    key={item.object_guid || idx}
                                                    onClick={() => setSelectedObject({ type: kind, name: item.display_name || item.name, data: item })}
                                                    className="hover:bg-gray-800/60 cursor-pointer transition-colors"
                                                >
                                                    <td className="p-2.5 font-bold text-white flex items-center gap-2">
                                                        {icon}
                                                        <span>{item.display_name || item.name}</span>
                                                    </td>
                                                    <td className="p-2.5">
                                                        <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${badgeCls}`}>
                                                            {typeLabel}
                                                        </span>
                                                    </td>
                                                    <td className="p-2.5 font-mono text-gray-300">
                                                        {item.sam_account_name || item.dns_host_name || '-'}
                                                    </td>
                                                    <td className="p-2.5 font-mono text-gray-200 max-w-xs truncate">
                                                        {item.distinguished_name || '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        })()}
    </div>
</div>
);
}
